import type { EnhancedTocItem } from '../../types/navigation';

/**
 * Utility functions for Table of Contents operations
 */

/**
 * Flatten a nested ToC structure into a flat array
 */
export function flattenToc(toc: EnhancedTocItem[], level: number = 0): Array<EnhancedTocItem & { level: number }> {
  const flat: Array<EnhancedTocItem & { level: number }> = [];

  toc.forEach((item) => {
    flat.push({ ...item, level });

    if (item.subitems?.length) {
      flat.push(...flattenToc(item.subitems, level + 1));
    }
  });

  return flat;
}

/**
 * Search ToC items by label (case-insensitive)
 */
export function searchToc(toc: EnhancedTocItem[], query: string): EnhancedTocItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return toc;

  const results: EnhancedTocItem[] = [];

  for (const item of toc) {
    const filteredSubitems = item.subitems ? searchToc(item.subitems, query) : [];
    const matches = item.label.toLowerCase().includes(normalizedQuery);

    if (matches || filteredSubitems.length > 0) {
      results.push({
        ...item,
        subitems: filteredSubitems.length > 0 ? filteredSubitems : undefined,
      });
    }
  }

  return results;
}

/**
 * Extract navigation levels (hrefs grouped by depth)
 */
export function extractNavigationLevels(toc: EnhancedTocItem[]): Record<number, string[]> {
  const levels: Record<number, string[]> = {};

  const walk = (items: EnhancedTocItem[], level: number) => {
    if (!items.length) return;
    if (!levels[level]) levels[level] = [];

    items.forEach((item) => {
      levels[level].push(item.href);
      if (item.subitems) {
        walk(item.subitems, level + 1);
      }
    });
  };

  walk(toc, 0);
  return levels;
}

/**
 * Build a lookup map for fast ToC access by href and id
 * Items are indexed by BOTH their base href AND their full id (with fragment)
 */
export function buildTocLookupMap(toc: EnhancedTocItem[]): Record<string, EnhancedTocItem> {
  const map: Record<string, EnhancedTocItem> = {};

  const walk = (items: EnhancedTocItem[]) => {
    items.forEach((item) => {
      // Index by base href
      map[item.href] = item;

      // ALSO index by id (which includes fragment) for precise lookup
      if (item.id && item.id !== item.href) {
        map[item.id] = item;
      }

      if (item.subitems?.length) {
        walk(item.subitems);
      }
    });
  };

  walk(toc);
  return map;
}

/**
 * Count total ToC items (including nested)
 * Works with both NavItem and EnhancedTocItem types
 */
export function countTocItems<T extends { subitems?: T[] }>(toc: T[]): number {
  return toc.reduce((acc, item) => {
    return acc + 1 + (item.subitems ? countTocItems(item.subitems) : 0);
  }, 0);
}
